var ColumnatedListArranger              = require("app/ui/repeaters/ColumnatedListArranger"),
    PageThumbnail                       = require("./PageThumbnail"),
    PageThumbnailCarouselDataAdapter    = require("./PageThumbnailCarouselDataAdapter"),
    PageThumbnailPlaceholder            = require("./PageThumbnailPlaceholder"),
    RepeaterDrawLoopSupport             = require("enyo-ui/RepeaterDrawLoopSupport"),
    VirtualizedRepeater                 = require("enyo-ui/VirtualizedRepeater"),
    VirtualizedRepeaterDrawAheadSupport = require("enyo-ui/VirtualizedRepeaterDrawAheadSupport"),
    kind                                = require("enyo/kind"),
    themeVars                           = require("app/theme/variables");

/**
 * [PageThumbnailList]{@link app.MediaPlayer.Tech.ImageReader.PageThumbnailList}
 * is a horizontally scrolling virtualized lists of
 * [PageThumbnails]{@link app.MediaPlayer.Tech.ImageReader.PageThumbnail}.
 *
 * @ui
 * @class   app.MediaPlayer.Tech.ImageReader.PageThumbnailList
 * @extends enyo-ui.VirtualizedRepeater
 * @public
 */
module.exports = kind(/** @lends  app.MediaPlayer.Tech.ImageReader.PageThumbnailList.prototype */ {

    /**
     * @private
     */
    name: "app.MediaPlayer.Tech.ImageReader.PageThumbnailList",

    /**
     * @private
     */
    kind: VirtualizedRepeater,

    /**
     * @private
     */
    mixins: [
        PageThumbnailCarouselDataAdapter,
        RepeaterDrawLoopSupport,
        VirtualizedRepeaterDrawAheadSupport
    ],

    /**
     * @private
     */
    classes: "app-media-player-image-reader-page-thumbnail-list",

    /**
     * The dataset
     *
     * @type  {?Object}
     * @public
     */
    data: null,

    /**
     * Simple array support, refreshes the entire view when data changes.
     *
     * @type {app.MediaPlayer.Tech.ImageReader.PageThumbnailCarouselDataAdapter}
     * @public
     */
    dataAdapterKind: PageThumbnailCarouselDataAdapter,

    /**
     * The kind responsible for arranging children within the
     * childContainer.
     *
     * @type  {enyo-ui.RepeaterArranger}
     * @public
     */
    arrangerKind: ColumnatedListArranger,

    /**
     * Options for the arranger.
     *
     * @type  {Object=}
     * @public
     */
    arrangerOptions: {
        direction: "horizontal",

        /**
         * Calculate spacing based on current rem-to-px value
         *
         * @return  {number}  The amount of pixels in 1 rem
         * @public
         */
        childSpacing: function() {
            return themeVars.getGridInnerSpacingHorizontalPx();
        },

        /**
         * Calculate spacing based on current rem-to-px value
         *
         * @return  {number}  The amount of pixels in 1 rem
         * @public
         */
        padBefore: function() {
            return themeVars.safeAreaInsetLeft + themeVars.getPageMarginPx();
        },

        /**
         * Calculate spacing based on current rem-to-px value
         *
         * @return  {number}  The amount of pixels in 1 rem
         * @public
         */
        padAfter: function() {
            return themeVars.safeAreaInsetRight + themeVars.getPageMarginPx();
        },

        /**
         * Ensure at least 2 columns.
         *
         * @returns  {number}  The maximum width
         * @public
         */
        maxChildWidth: function() {
            var viewportWidth = this.getOption("viewportWidth"),
                padBefore     = this.getOption("padBefore"),
                padAfter      = this.getOption("padAfter"),
                childSpacing  = this.getOption("childSpacing"),
                availableWidth;

            availableWidth = viewportWidth
                + childSpacing // One childSpacing worth of overflow
                - padBefore // Compensate for left padding
                - padAfter; // Compensate for right padding

            return Math.min(availableWidth / 3, 210);
        },

        /**
         * Ensure the children don't break out of the bounds of the reader.
         *
         * @returns  {number}  The maximum height
         * @public
         */
        maxChildHeight: function() {
            var canvasView   = this.repeater.get("canvasView"),
                canvasNode   = canvasView && canvasView.hasNode(),
                canvasHeight = canvasNode ? canvasNode.offsetHeight : 0;

            return canvasHeight;
        },
        roundPixels: true
    },

    /**
     * The kind that will be rendered for each item in the dataset.
     *
     * @type  {enyo.Control}
     * @public
     */
    childKind: PageThumbnail,

    /**
     * The kind that will be rendered for each placeholder.
     *
     * @type  {enyo.Control}
     * @public
     */
    placeholderKind: PageThumbnailPlaceholder,

    /**
     * Whether the drawing process should be asynchronous, drawing one child
     * at at time.
     *
     * @type  {boolean}
     * @public
     */
    drawAsync: true,

    /**
     * The minimum width for the draw bounds.
     *
     * @type  {number}
     * @public
     */
    drawAheadMinWidth: 1080,

    /**
     * The amount of pages that should be drawn ahead.
     *
     * @type  {number}
     * @public
     */
    drawAheadPagesX: 1.5,

    /**
     * Extra options to mix into the scroller instance.
     *
     * @type  {Object=}
     * @public
     */
    scrollerOptions: {
        classes:        "app-media-player-image-reader-page-thumbnail-list__scroller",
        vertical:       false,
        horizontal:     true,
        scrollChaining: false,
        maxWidth:       "100%"
    },

    /**
     * Extend child creation by setting the `active` flag if a child is created
     * or the currently active index.
     *
     * @param  {*}             record  The record
     * @return {enyo.Control}  The created child
     * @protected
     */
    createChild: kind.inherit(function(sup) {
        return function(record) {
            var child;

            child = sup.apply(this, arguments);

            // Mark active child as active
            if (this.getRecordByIndex(this.activeIndex) === record) {
                child.addClass("active");
            }

            return child;
        };
    }),

    /**
     * Activate the child for the active index, deactivate the previously active
     * child. Then scroll the active child to the center of the scroller.
     *
     * @param  {number=}  oldIndex  The previously active index
     * @private
     */
    activeIndexChanged: function(oldIndex) {
        var activeIndex = this.activeIndex,
            activeChild,
            oldChild;

        if (oldIndex !== activeIndex) {
            if (oldIndex || oldIndex === 0) {
                oldChild = this.getChildForIndex(oldIndex);
                oldChild && oldChild.removeClass("active");
            }

            if (activeIndex || activeIndex === 0) {
                activeChild = this.getChildForIndex(activeIndex);
                activeChild && activeChild.addClass("active");

                if (this.absoluteShowing) {
                    this.scrollToIndex(this.activeIndex);
                }
                else {
                    this.set("anchor", {
                        index:      this.activeIndex,
                        offsetLeft: 0,
                        offsetTop:  0
                    });
                }
            }
        }
    },

    /**
     * Figures out which index is currently located closest to the center of the
     * viewport as well as it's offset relative to a fully centered position
     * within the viewport.
     *
     * By default we assume the top-left corner of the childContainer aligns
     * with the top-lef corner of the scroller. Override this method if this is
     * not the case.
     *
     * @public
     */
    updateAnchor: function() {
        var activeIndex        = this.activeIndex,
            arrangementBounds,
            arrangementIndexes,
            scroller           = this.getScroller(),
            scrollerDimensions = scroller.get("dimensions"),
            scrollPosition     = scroller.get("scrollPosition"),
            index,
            offsetLeft,
            offsetTop;

        arrangementBounds  = this.getArrangementBounds();
        arrangementIndexes = this.getArrangementIndexes(arrangementBounds);

        // Active index is visible. Use it as anchor.
        if (arrangementIndexes.indexOf(activeIndex) > -1) {
            this.set("anchor", {
                index:      activeIndex,
                offsetLeft: 0,
                offsetTop:  0
            });
        }

        // Active index is not visible. Use the item closest to the center
        // instead.
        else {
            index      = this.arranger.getNearestIndex(
                scrollPosition.scrollLeft + scrollerDimensions.scrollerWidth / 2,
                scrollPosition.scrollTop + scrollerDimensions.scrollerHeight / 2
            );
            offsetLeft = scrollPosition.scrollLeft - this.calcCenterScrollLeft(index);
            offsetTop  = scrollPosition.scrollTop - this.arranger.getScrollTop(index);

            this.set("anchor", {
                index:      index,
                offsetLeft: offsetLeft,
                offsetTop:  offsetTop
            });
        }
    },

    /**
     * Snaps the repeater to center on the current anchor.
     *
     * @public
     */
    snapToAnchor: function() {
        var scroller = this.getScroller(),
            arranger = this.arranger,
            anchor   = this.anchor;

        if (!scroller.scrolling) {
            scroller.snapTo(
                this.calcCenterScrollLeft(anchor.index) + anchor.offsetLeft,
                arranger.getScrollTop(anchor.index) + anchor.offsetTop
            );
        }
    },

    /**
     * Scrolls the repeater to center the item at the provided index.
     *
     * @param  {number}  index  The index
     * @public
     */
    scrollToIndex: function(index) {
        var scroller = this.getScroller(),
            arranger = this.arranger;

        scroller.scrollTo(
            this.calcCenterScrollLeft(index),
            arranger.getScrollTop(index)
        );
    },

    /**
     * Snaps the repeater to center the item at the provided index.
     *
     * @param  {number}  index  The index
     * @public
     */
    snapToIndex: function(index) {
        var scroller = this.getScroller(),
            arranger = this.arranger;

        scroller.snapTo(
            this.calcCenterScrollLeft(index),
            arranger.getScrollTop(index)
        );
    },

    /**
     * Calculates the scroll offset along the horizontal axis required to
     * horizontally center the item for the provided index in the scroller.
     *
     * @param  {number}  index  The index
     * @return {number}  The scroll offset
     * @private
     */
    calcCenterScrollLeft: function(index) {
        var scrollerDimensions = this.getScroller().get("dimensions"),
            arranger           = this.arranger,
            scrollLeft         = arranger.getScrollLeft(index),
            scrollLeftNext     = arranger.getScrollLeft(index + 1),
            target;

        target = scrollLeft;
        target -= scrollerDimensions.scrollerWidth / 2;
        target += (scrollLeftNext - scrollLeft) / 2;
        target += arranger.getOption("padBefore") / 2;

        return target;
    }

});
